import { PLAYER_LIVE_PREVIEW_MAX_WIDTH_PX } from '@lucimark/shared/helpers/playerLivePreview'

const MIN_VISIBLE_MEDIA_PX = 32

export interface TLivePreviewMediaCandidate {
  source: CanvasImageSource
  width: number
  height: number
  area: number
}

export function isMediaElementVisibleForLivePreview(el: Element): boolean {
  const style = window.getComputedStyle(el)
  if (style.visibility === 'hidden' || style.display === 'none' || Number(style.opacity) === 0) {
    return false
  }
  const rect = el.getBoundingClientRect()
  return rect.width >= MIN_VISIBLE_MEDIA_PX && rect.height >= MIN_VISIBLE_MEDIA_PX
}

export function collectVisibleLivePreviewMedia(root: Element): TLivePreviewMediaCandidate[] {
  const candidates: TLivePreviewMediaCandidate[] = []

  for (const video of root.querySelectorAll('video')) {
    if (!(video instanceof HTMLVideoElement)) {
      continue
    }
    if (!isMediaElementVisibleForLivePreview(video)) {
      continue
    }
    if (video.readyState < 2 || video.videoWidth <= 0 || video.videoHeight <= 0) {
      continue
    }
    const rect = video.getBoundingClientRect()
    candidates.push({
      source: video,
      width: video.videoWidth,
      height: video.videoHeight,
      area: rect.width * rect.height
    })
  }

  for (const img of root.querySelectorAll('img')) {
    if (!(img instanceof HTMLImageElement)) {
      continue
    }
    if (!isMediaElementVisibleForLivePreview(img)) {
      continue
    }
    if (!img.complete || img.naturalWidth <= 0 || img.naturalHeight <= 0) {
      continue
    }
    const rect = img.getBoundingClientRect()
    candidates.push({
      source: img,
      width: img.naturalWidth,
      height: img.naturalHeight,
      area: rect.width * rect.height
    })
  }

  return candidates.sort((a, b) => b.area - a.area)
}

function isCanvasExportSecurityError(err: unknown): boolean {
  if (!(err instanceof DOMException)) {
    return false
  }
  return err.name === 'SecurityError' || err.code === 18
}

function scaleDimensions(width: number, height: number, maxWidth: number): { width: number, height: number } {
  if (width <= maxWidth) {
    return { width, height }
  }
  const scale = maxWidth / width
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale))
  }
}

async function renderMediaCandidateToJpegBlob(
  media: TLivePreviewMediaCandidate
): Promise<Blob | null> {
  const scaled = scaleDimensions(media.width, media.height, PLAYER_LIVE_PREVIEW_MAX_WIDTH_PX)
  const canvas = document.createElement('canvas')
  canvas.width = scaled.width
  canvas.height = scaled.height
  const ctx = canvas.getContext('2d')
  if (ctx == null) {
    return null
  }
  try {
    ctx.drawImage(media.source, 0, 0, scaled.width, scaled.height)
  }
  catch {
    return null
  }
  try {
    return await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(
        blob => resolve(blob),
        'image/jpeg',
        0.78
      )
    })
  }
  catch (err) {
    if (isCanvasExportSecurityError(err)) {
      return null
    }
    throw err
  }
}

/** Captures the current on-screen frame as a JPEG blob, or null when nothing drawable is visible. */
export async function capturePlayerLivePreviewBlob(): Promise<Blob | null> {
  const root = document.querySelector('[data-player-live-preview-root]') as HTMLElement | null
  if (root == null) {
    return null
  }
  const candidates = collectVisibleLivePreviewMedia(root)
  for (const media of candidates) {
    const blob = await renderMediaCandidateToJpegBlob(media)
    if (blob != null && blob.size > 0) {
      return blob
    }
  }
  return null
}
